import { useEffect, useState, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ClipboardCheck, CalendarDays, BarChart3, MessageSquarePlus, LogOut, Sparkles, FolderOpen, BookOpen, FolderKanban, KeyRound } from "lucide-react";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import NotificationBell from "../components/NotificationBell";
import RequestDialog from "../components/RequestDialog";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmProvider";

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [requests, setRequests] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const reload = async () => {
    try {
      const [s, a, r] = await Promise.allSettled([
        api.get("/portal/summary"),
        api.get("/portal/analytics"),
        api.get("/requests"),
      ]);
      if (s.status === "fulfilled") setSummary(s.value.data);
      if (a.status === "fulfilled") setAnalytics(a.value.data);
      if (r.status === "fulfilled") setRequests(r.value.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(()=>{ reload(); }, []);

  const pendingCount = summary?.stats?.pending_approvals?.length || 0;
  const openReqCount = useMemo(()=>requests.filter(r=>r.status==="Open").length, [requests]);

  const NAV = [
    { to: "/portal", label: "Overview", icon: LayoutDashboard, testid: "portal-nav-overview", end: true },
    { to: "/portal/approvals", label: "Approvals", icon: ClipboardCheck, testid: "portal-nav-approvals", badge: pendingCount },
    { to: "/portal/calendar", label: "Calendar", icon: CalendarDays, testid: "portal-nav-calendar" },
    { to: "/portal/analytics", label: "Post Analytics", icon: BarChart3, testid: "portal-nav-analytics" },
    { to: "/portal/strategy", label: "Strategy", icon: BookOpen, testid: "portal-nav-strategy" },
    { to: "/portal/library", label: "Content Library", icon: FolderOpen, testid: "portal-nav-library" },
    { to: "/portal/projects", label: "Projects", icon: FolderKanban, testid: "portal-nav-projects" },
    { to: "/portal/requests", label: "Creative Requests", icon: MessageSquarePlus, testid: "portal-nav-requests", badge: openReqCount },
  ];

  const client = summary?.client;
  const brand = client?.brand_color || "";
  const accentStyle = brand ? { background: brand } : {};
  const accentGradient = brand
    ? { background: `linear-gradient(135deg, ${brand}, ${brand}CC)` }
    : {};
  const activeBg = brand ? `${brand}1A` : "";  // 10% opacity for active nav bg
  const activeBorder = brand ? `${brand}4D` : ""; // 30% opacity
  const initials = (user?.name || "?").split(" ").map(x=>x[0]).slice(0,2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-[#F5F7FB] text-[#0F1729] relative" data-testid="portal-shell">
      <aside className="w-64 shrink-0 border-r border-[#E4E7EC] bg-[#FFFFFF]/80 backdrop-blur-md sticky top-0 h-screen flex flex-col z-10">
        <div className="px-6 py-5 border-b border-[#E4E7EC] flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg grid place-items-center" style={brand ? accentGradient : { background: "linear-gradient(135deg,#F97316,#DC2626)" }}>
            <Sparkles className="w-4 h-4 text-white"/>
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">{client?.brand_name || client?.client_name || "Client"}<span className="text-neutral-400 font-normal ml-1">Portal</span></div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Powered by Shloka</div>
          </div>
        </div>

        {client && (
          <div className="px-4 py-4 border-b border-[#E4E7EC]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg grid place-items-center text-sm font-extrabold text-white" style={brand ? accentGradient : { background: "linear-gradient(135deg,#F9731666,#DC262633)" }} data-testid="portal-client-avatar">
                {(client.brand_name || client.client_name || "?").slice(0,2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" data-testid="portal-client-name">{client.client_name}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 truncate">{client.industry}</div>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              style={({isActive}) => isActive && brand ? { background: activeBg, borderColor: activeBorder, color: brand } : {}}
              className={({isActive}) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? (brand ? "border" : "bg-orange-100 text-orange-700 border border-orange-500/30")
                    : "text-neutral-600 hover:text-neutral-900 hover:bg-black/[0.04] border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="flex-1">{n.label}</span>
              {n.badge > 0 && (
                <span
                  className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${brand ? "" : "bg-orange-500/20 text-orange-700 border-orange-500/30"}`}
                  style={brand ? { background: activeBg, color: brand, borderColor: activeBorder } : {}}
                  data-testid={`${n.testid}-badge`}
                >
                  {n.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 pb-3">
          <Button onClick={()=>setReqOpen(true)} data-testid="portal-request-creative-btn" className="w-full text-white hover:opacity-90" style={brand ? accentGradient : { background: "linear-gradient(135deg,#F97316,#DC2626)" }}>
            <Sparkles className="w-4 h-4 mr-1"/> Request Creative
          </Button>
        </div>

        <div className="p-3 border-t border-[#E4E7EC]">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 grid place-items-center text-[11px] font-bold shrink-0 text-white">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase text-neutral-500 truncate">Client</div>
            </div>
            <NotificationBell/>
            <Button size="icon" variant="ghost" onClick={() => setPwOpen(true)} title="Change password" data-testid="portal-change-password-btn">
              <KeyRound className="w-4 h-4"/>
            </Button>
            <Button size="icon" variant="ghost" title="Logout" onClick={async ()=>{ if (!(await confirm({ title: "Are you sure you want to logout?", confirmText: "Logout", destructive: false }))) return; await logout(); navigate("/login"); }} data-testid="portal-logout">
              <LogOut className="w-4 h-4"/>
            </Button>
          </div>
        </div>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      </aside>

      <main className="flex-1 relative z-[2]" key={location.pathname}>
        <div className="grid-lines absolute inset-0 opacity-30 pointer-events-none"/>
        <div className="relative">
          <Outlet context={{ summary, analytics, requests, reload, openRequestDialog: ()=>setReqOpen(true), brandColor: brand }}/>
        </div>
      </main>

      <RequestDialog open={reqOpen} onOpenChange={setReqOpen} onSaved={reload}/>
    </div>
  );
}
